(() => {
  'use strict';

  const identifier = value => typeof value === 'string' && value.trim().length > 0 && value.length <= 128;
  const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);

  function configuration(value) {
    if (!object(value) || value.version !== 1 || typeof value.enabled !== 'boolean' ||
      value.offerId !== 'grounding-pdp-v1' || !positiveInteger(value.productId) ||
      !Array.isArray(value.eligibleProductIds) || !value.eligibleProductIds.length ||
      !value.eligibleProductIds.every(positiveInteger) || !value.eligibleProductIds.includes(value.productId) ||
      new Set(value.eligibleProductIds).size !== value.eligibleProductIds.length ||
      !object(value.gifts) || !Array.isArray(value.tiers) || value.tiers.length !== 3 ||
      !Object.keys(value.gifts).length || !Object.entries(value.gifts).every(([key, id]) => identifier(key) && positiveInteger(id)) ||
      new Set(Object.values(value.gifts)).size !== Object.keys(value.gifts).length) return null;
    const quantities = [1, 2, 4];
    if (!value.tiers.every((tier, index) => object(tier) && tier.minimumQuantity === quantities[index] &&
      Number.isSafeInteger(tier.percentage) && tier.percentage >= 0 && tier.percentage <= 100 &&
      Array.isArray(tier.gifts) && new Set(tier.gifts).size === tier.gifts.length &&
      tier.gifts.every(key => Object.hasOwn(value.gifts, key)))) return null;
    return value;
  }

  function tierFor(config, quantity) {
    return positiveInteger(quantity) ? config.tiers.findLast(tier => quantity >= tier.minimumQuantity) : undefined;
  }

  function selection(variants, rows, colorIndex, sizeIndex) {
    return rows.map(({color, size}) => {
      const variant = variants.find(item => item.options[colorIndex] === color && item.options[sizeIndex] === size);
      if (!variant?.available) throw new Error('unavailable');
      return variant;
    });
  }

  function quote(config, variants) {
    const tier = tierFor(config, variants.length);
    if (!tier || !variants.every(variant => positiveInteger(variant.id) &&
      Number.isSafeInteger(variant.price) && variant.price >= 0 &&
      (variant.compare_at_price == null || Number.isSafeInteger(variant.compare_at_price)))) throw new Error('configuration');
    const total = variants.reduce((sum, variant) => sum + variant.price - Math.floor(variant.price * tier.percentage / 100), 0);
    const original = variants.reduce((sum, variant) => sum + Math.max(variant.price, variant.compare_at_price || 0), 0);
    return {total, original, savings: original - total, unit: Math.round(total / variants.length)};
  }

  function items(config, variants, giftCatalog, instance) {
    const tier = tierFor(config, variants.length);
    if (!config.enabled || !tier || !identifier(instance) || ![1, 2, 4].includes(variants.length)) throw new Error('configuration');
    const properties = {_cn_offer_id: config.offerId, _cn_offer_instance: instance, _cn_offer_role: 'qualifying'};
    const paid = new Map();
    for (const variant of variants) {
      if (!positiveInteger(variant.id) || !variant.available) throw new Error('unavailable');
      const item = paid.get(variant.id) || {id: variant.id, quantity: 0, properties: {...properties}};
      item.quantity++;
      paid.set(variant.id, item);
    }
    const gifts = tier.gifts.map(key => {
      const variantId = config.gifts[key];
      if (!giftCatalog.some(gift => gift.id === variantId && gift.available)) throw new Error('gift_unavailable');
      return {id: variantId, quantity: 1, properties: {
        _cn_offer_id: config.offerId, _cn_offer_instance: instance, _cn_offer_role: 'gift', _cn_offer_gift: key
      }};
    });
    return [...paid.values(), ...gifts];
  }

  function giftUpdates(config, cart) {
    if (!config?.enabled) return {};
    const groups = new Map();
    for (const item of cart.items || []) {
      const props = item.properties || {};
      if (props._cn_offer_id !== config.offerId || !identifier(props._cn_offer_instance)) continue;
      if (['_landing_offer_campaign', '_landing_offer_instance', '_grounding_landing_campaign',
        '_calm_kit_campaign', '_offer_id', '_offer_payload'].some(key => props[key] != null)) continue;
      const group = groups.get(props._cn_offer_instance) || {quantity: 0, gifts: [], invalid: false};
      groups.set(props._cn_offer_instance, group);
      if (props.__kaching_bundles != null) group.invalid = true;
      if (props._cn_offer_role === 'qualifying') {
        if (!config.eligibleProductIds.includes(Number(item.product_id)) || !positiveInteger(item.quantity) || props._cn_offer_gift != null) group.invalid = true;
        else group.quantity += item.quantity;
      } else if (props._cn_offer_role === 'gift') group.gifts.push(item);
      else group.invalid = true;
    }
    const updates = {};
    for (const group of groups.values()) {
      const tier = group.invalid ? undefined : tierFor(config, group.quantity);
      const seen = new Set();
      for (const item of group.gifts) {
        const key = item.properties._cn_offer_gift;
        const earned = tier?.gifts.includes(key) && config.gifts[key] === Number(item.variant_id) &&
          positiveInteger(item.quantity) && !seen.has(key);
        if (earned) seen.add(key);
        const quantity = earned ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
    }
    return updates;
  }

  function money(cents, format) {
    const formats = {
      amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'], amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','], amount_with_period_and_space_separator: [2, ' ', '.']
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match || !formats[match[1]]) throw new Error('configuration');
    const [precision, group, decimal] = formats[match[1]];
    const parts = (cents / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, group);
    const template = document.createElement('template');
    template.innerHTML = format.replace(match[0], parts.join(decimal));
    return template.content.textContent;
  }

  const node = document.querySelector('[data-grounding-offer-config]');
  let config = null;
  try { config = configuration(JSON.parse(node?.textContent || 'null')); } catch {}
  window.GroundingOffer = {config, configuration, tierFor, selection, quote, items, giftUpdates, money};
})();
